import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  listarPagamentosInscritosLiga,
  type PagamentoInscritosLiga,
} from '../../../services/pagamentoInscritosLigaService';
import { formatCurrency } from '../../../utils/money';
import LigaInscricoesTab from '../../liga/payments/LigaInscricoesTab';
import LigaRecebimentosTab from '../../liga/payments/LigaRecebimentosTab';
import AdminLigaPagamentosSubTab from './AdminLigaPagamentosSubTab';

type TabId = 'inscricoes' | 'recebimentos' | 'pagamentos';

const TABS: { id: TabId; label: string }[] = [
  { id: 'inscricoes', label: 'Inscrições' },
  { id: 'recebimentos', label: 'Recebimentos' },
  { id: 'pagamentos', label: 'Pagamentos' },
];

interface AdminLigaPaymentsTabProps {
  ligaId: string;
}

const AdminLigaPaymentsTab: React.FC<AdminLigaPaymentsTabProps> = ({ ligaId }) => {
  const [activeTab, setActiveTab] = useState<TabId>('pagamentos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagamentos, setPagamentos] = useState<PagamentoInscritosLiga[]>([]);

  const fetchPagamentos = useCallback(async () => {
    if (!ligaId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    try {
      const result = await listarPagamentosInscritosLiga({ ligaId, size: 9999 });
      if (!cancelled) {
        setPagamentos(result.items);
      }
    } catch (e: any) {
      if (!cancelled) {
        const msg =
          e?.graphQLErrors?.[0]?.message ||
          e?.message ||
          'Erro ao carregar pagamentos. Tente novamente.';
        setError(msg);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [ligaId]);

  useEffect(() => {
    fetchPagamentos();
  }, [fetchPagamentos]);

  const stats = useMemo(() => {
    const cobrancasGeradas = pagamentos.length;
    const cobrancasPagas = pagamentos.filter((p) => p.status === 'PAGO').length;
    const cobrancasEmAberto = pagamentos.filter((p) => p.status === 'PENDENTE').length;
    const totalAReceber = pagamentos.reduce((sum, p) => sum + p.valor, 0);
    const totalRecebido = pagamentos
      .filter((p) => p.status === 'PAGO')
      .reduce((sum, p) => sum + p.valor, 0);
    const totalPendente = pagamentos
      .filter((p) => p.status === 'PENDENTE')
      .reduce((sum, p) => sum + p.valor, 0);

    return {
      cobrancasGeradas,
      cobrancasPagas,
      cobrancasEmAberto,
      totalAReceber,
      totalRecebido,
      totalPendente,
    };
  }, [pagamentos]);

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <>
      {/* Statistics Cards */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      ) : (
        <div className="row mb-4">
          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="card-title text-muted mb-2">Cobranças Geradas</h5>
                <h2 className="fw-700 mb-1">{stats.cobrancasGeradas}</h2>
                <p className="text-muted mb-0 small">total de cobranças</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="card-title text-muted mb-2">Cobranças Pagas</h5>
                <h2 className="fw-700 mb-1">{stats.cobrancasPagas}</h2>
                <p className="text-muted mb-0 small">cobranças com status pago</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="card-title text-muted mb-2">Cobranças Em Aberto</h5>
                <h2 className="fw-700 mb-1">{stats.cobrancasEmAberto}</h2>
                <p className="text-muted mb-0 small">cobranças pendentes</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="card-title text-muted mb-2">Total a Receber</h5>
                <h2 className="fw-700 mb-1">{formatCurrency(stats.totalAReceber)}</h2>
                <p className="text-muted mb-0 small">valor total</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="card-title text-muted mb-2">Total Recebido</h5>
                <h2 className="fw-700 mb-1">{formatCurrency(stats.totalRecebido)}</h2>
                <p className="text-muted mb-0 small">valor recebido</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="card-title text-muted mb-2">Total Pendente</h5>
                <h2 className="fw-700 mb-1">{formatCurrency(stats.totalPendente)}</h2>
                <p className="text-muted mb-0 small">valor pendente</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nav Tabs */}
      <ul className="nav nav-tabs mb-3" role="tablist">
        {TABS.map((tab) => (
          <li key={tab.id} className="nav-item" role="presentation">
            <button
              type="button"
              className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              role="tab"
              aria-selected={activeTab === tab.id}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Tab Content */}
      {activeTab === 'pagamentos' ? (
        <AdminLigaPagamentosSubTab ligaId={ligaId} onDataChanged={fetchPagamentos} />
      ) : activeTab === 'recebimentos' ? (
        <LigaRecebimentosTab ligaId={ligaId} />
      ) : (
        <LigaInscricoesTab ligaId={ligaId} />
      )}
    </>
  );
};

export default AdminLigaPaymentsTab;
